import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowRight, Briefcase, Truck, Thermometer, Hammer, Layers } from 'lucide-react';
import { DOMAIN, BRAND } from '../constants';
import { Reveal } from '../components/UI';

export const Home: React.FC = () => {
  const pageTitle = "Béton Mobile & Plancher Radiant Granby | Béton Mobile des Cantons";
  const pageDesc = "Expert en béton fabriqué sur chantier et planchers radiants à Granby depuis 1983. Économisez avec notre béton mobile et nos systèmes de chauffage. Soumission gratuite !";
  const canonicalUrl = `${DOMAIN}/`;

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "GeneralContractor",
    "name": BRAND,
    "url": DOMAIN,
    "logo": `${DOMAIN}/images/logo-beton-mobile-des-cantons-fr.png`,
    "description": "Béton Mobile des Cantons offre du béton fabriqué sur chantier et des planchers radiants de haute qualité depuis 1983.",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "875, rue Dufferin",
      "addressLocality": "Granby",
      "addressRegion": "QC",
      "postalCode": "J1H 0T7",
      "addressCountry": "CA"
    },
    "telephone": "+14503722565",
    "openingHours": "Mo-Fr 07:00-17:00",
    "areaServed": ["Granby", "Haute-Yamaska", "Cantons-de-l'Est", "Estrie", "Montérégie"]
  };

  return (
    <div className="bg-slate-50">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDesc} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDesc} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={`${DOMAIN}/images/logo-beton-mobile-des-cantons-fr.png`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDesc} />
        <meta name="twitter:image" content={`${DOMAIN}/images/logo-beton-mobile-des-cantons-fr.png`} />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>

      <div className="relative h-screen w-full overflow-hidden flex items-center">
        <div className="absolute inset-0 z-10 bg-[url('https://www.transparenttextures.com/patterns/graphy.png')] opacity-10 pointer-events-none"></div>
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1541888946425-d81bb19240f5?q=80&w=2070&auto=format&fit=crop" 
            alt="Beton chantier" 
            className="w-full h-full object-cover" 
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-900/80 to-slate-900/30"></div>
        </div>
        <div className="relative z-20 container mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center pt-20">
          <Reveal>
            <div className="border-l-8 border-brand-red pl-8 md:pl-12 py-4 bg-slate-900/20 backdrop-blur-sm">
              <span className="text-brand-red font-mono font-bold tracking-[0.2em] uppercase mb-4 block text-sm">Expertise depuis 1983</span>
              <h1 className="text-4xl md:text-5xl lg:text-7xl font-display font-bold text-white mb-6 uppercase leading-[1.1]">
                Béton Fabriqué <br/>sur Chantier <br/><span className="text-slate-400 stroke-text text-2xl md:text-4xl lg:text-5xl block mt-2">Plancher Radiant - Granby</span>
              </h1>
              <p className="text-lg text-slate-300 max-w-lg mb-10 font-light border-t border-white/20 pt-6">
                Projets clés en main • Béton Mobile • Plancher radiant • Location d'équipement • Produits
              </p>
              <div className="flex flex-col sm:flex-row gap-0">
                <Link to="/contact" className="px-10 py-4 bg-brand-red text-white font-display font-bold uppercase tracking-wider text-center hover:bg-brand-red-dark transition-all duration-300 border-2 border-brand-red">
                  Demande de soumission
                </Link>
                <Link to="/services" className="px-10 py-4 bg-transparent border-2 border-white text-white font-display font-bold uppercase tracking-wider text-center hover:bg-white hover:text-slate-900 transition-all duration-300">
                  Nos Services
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
        <div className="absolute bottom-0 right-0 hidden lg:flex bg-slate-900 text-white w-auto max-w-2xl border-t-4 border-brand-red">
           <div className="p-8 border-r border-slate-700 flex-1">
             <p className="text-4xl font-mono font-bold text-white mb-1">25k+</p>
             <p className="text-xs text-brand-red font-bold uppercase tracking-widest">Projets Réalisés</p>
           </div>
           <div className="p-8 border-r border-slate-700 flex-1">
             <p className="text-4xl font-mono font-bold text-white mb-1">24/7</p>
             <p className="text-xs text-brand-red font-bold uppercase tracking-widest">Disponibilité</p>
           </div>
           <Link to="/contact" className="p-8 flex-1 bg-brand-red flex flex-col justify-center items-center cursor-pointer hover:bg-brand-red-dark transition-colors text-white no-underline">
             <span className="font-display font-bold uppercase text-sm">Contact Rapide</span>
             <ArrowRight className="mt-2"/>
           </Link>
        </div>
      </div>

      <div className="bg-slate-800 text-white py-2 text-center font-mono text-xs tracking-wide uppercase border-b border-slate-700 flex justify-center items-center">
        <div className="w-2 h-2 bg-green-500 rounded-full mr-3 animate-pulse"></div> 
        Annonce : Nous sommes ouvert pendant les vacances de la construction 2018
      </div>

      <section className="py-24 bg-white relative bg-tech-grid">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-12 gap-16 items-start">
            <div className="lg:col-span-5 relative">
              <Reveal>
                <h2 className="text-5xl font-display font-bold text-slate-900 mb-8 leading-[0.9] uppercase">
                  Qualité & <span className="text-brand-red block">Précision</span> <span className="text-slate-400 text-3xl">Depuis 1983</span>
                </h2>
                <div className="w-20 h-2 bg-brand-red mb-8"></div>
                <div className="space-y-6 text-slate-600 font-light leading-relaxed text-justify">
                  <p>
                    <strong className="text-slate-900 font-bold">Depuis 1983</strong>, Béton Mobile des Cantons vous offre du béton fabriqué directement sur le chantier au lieu d'être produit en usine. Ainsi, les matières premières nécessaires à sa fabrication, telles que l'eau, la pierre concassée, le sable, la poudre de ciment et les additifs de béton, sont transportées dans la <strong className="text-brand-red">bétonnière mobile</strong> qui fabrique le béton à la force et à la densité voulues. De cette façon, le client paye seulement pour la quantité utilisée calculée au compteur.
                  </p>
                  <p>
                    Béton Mobile des Cantons vous offre également des <strong className="text-slate-900 font-bold">planchers radiants</strong> de première qualité. Cette nouvelle approche vous permet de réguler la température de vos pièces uniformément et d'économiser sur les frais de chauffage.
                  </p>
                </div>
              </Reveal>
            </div>
            <div className="lg:col-span-7">
              <Reveal delay={200}>
                <h3 className="text-2xl font-display font-bold text-slate-900 mb-6 uppercase tracking-wider flex items-center gap-3">
                  <span className="w-2 h-8 bg-brand-red"></span> Les services offerts
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border border-slate-200">
                  {[
                    { title: "Projets clés en main", icon: <Briefcase />, desc: "Gestion intégrale de votre projet." },
                    { title: "Béton Mobile", icon: <Truck />, desc: "Production sur chantier, zéro gaspillage." },
                    { title: "Plancher radiant", icon: <Thermometer />, desc: "Systèmes de chauffage confortables." },
                    { title: "Location d'équipement", icon: <Hammer />, desc: "Outils spécialisés et coffrage." },
                    { title: "Produits", icon: <Layers />, desc: "Concassé, sable, pierre, etc." },
                  ].map((s, i) => (
                    <div key={i} className="bg-white p-8 border-r border-b border-slate-200 hover:bg-slate-50 transition-colors group">
                      <div className="text-slate-400 group-hover:text-brand-red mb-4 transition-colors">{s.icon}</div>
                      <h3 className="font-display font-bold text-lg text-slate-900 uppercase tracking-wide mb-2">{s.title}</h3>
                      <p className="text-xs font-mono text-slate-500">{s.desc}</p>
                    </div>
                  ))}
                  <div className="bg-slate-50 p-8 border-r border-b border-slate-200 flex items-center justify-center opacity-50">
                     <img 
                       src="https://betonmobiledescantons.com/images/logo-beton-mobile-des-cantons-fr.png" 
                       alt="Logo" 
                       className="w-24 opacity-20 grayscale"
                     />
                  </div>
                </div>
                <Link to="/services" className="block bg-slate-900 text-white p-6 text-center font-display font-bold uppercase tracking-widest hover:bg-brand-red transition-colors mt-4">
                  Voir tous les détails <ArrowRight className="inline ml-2" size={16}/>
                </Link>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-brand-red relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-20"></div>
        <div className="container mx-auto px-6 relative z-10 flex flex-col md:flex-row items-center justify-between gap-8">
          <Reveal>
            <div>
              <h2 className="text-3xl font-display font-bold text-white mb-2 uppercase">Prêt à couler ?</h2>
              <p className="text-white/80 font-mono text-sm">Estimation gratuite. Réponse rapide. Service impeccable.</p>
            </div>
          </Reveal>
          <Reveal delay={100}>
             <Link to="/contact" className="inline-block bg-white text-brand-red px-8 py-4 font-display font-bold uppercase tracking-widest shadow-[4px_4px_0px_0px_rgba(0,0,0,0.3)] hover:shadow-none hover:translate-x-[2px] hover:translate-y-[2px] transition-all duration-200 border-2 border-white hover:bg-transparent hover:text-white">
               Contactez-nous
             </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
};